import { Grid, MenuItem, Select, TextField } from "@mui/material";
import * as React from "react";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { EventStateFilter } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { DrawerContent } from "../../framework/Drawer/DrawerContent";
import { FilterButtons } from "../../framework/Filters/FilterButtons";
import { FilterSearchField } from "../../framework/Filters/FilterSearchField";
import { FormSectionTitle } from "../../framework/Form/FormSectionTitle";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { PageFilterConditionDrawer } from "../../framework/Page/PageFilterConditionDrawer";
import { cloneEventPageFilterState, EventTypeNotSetId, IEventPageFilterLists, IEventPageFilterState, IEventPageFilterStateWithoutDates } from "./EventPageUtils";

export interface IEventPageFilterDrawerProp extends IEventPageFilterLists, IEventPageFilterState {
    open: boolean;
    classes?: string;
    filter: IListFilterItem;
    canRemoveSharedFilters: boolean;
    stateIsReadOnly?: boolean;
    onSaveFilter: (saveAs: boolean, onGetGeneralFilter: () => any) => void;
    onRemoveFilter: (filterId: string) => void;
    onOk: (closeDialog: boolean, filterState: IEventPageFilterState) => any;
    onCancel: () => void;
}

export interface IEventPageFilterDrawerState extends IEventPageFilterStateWithoutDates {
    startDateStr: string;
    endDateStr: string;
    startEndDateDisabled: boolean;
}

export class EventPageFilterDrawer extends React.Component<IEventPageFilterDrawerProp, IEventPageFilterDrawerState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IEventPageFilterDrawerProp): IEventPageFilterDrawerState => {
        return {
            startDateStr: props.selectedStartDate ? Base.timeToDateStr(props.selectedStartDate) : "",
            endDateStr: props.selectedEndDate ? Base.timeToDateStr(props.selectedEndDate) : "",
            selectedUnitIds: props.selectedUnitIds.slice(0),
            selectedStateIds: props.selectedStateIds.slice(0),
            selectedUrgencyClassIds: props.selectedUrgencyClassIds.slice(0),
            selectedEventTypeIds: props.selectedEventTypeIds.slice(0),
            selectedEventTypeBIds: props.selectedEventTypeBIds.slice(0),
            selectedEventTypeCIds: props.selectedEventTypeCIds.slice(0),
            selectedPartnerCompanyIds: props.selectedPartnerCompanyIds.slice(0),
            selectedCreateUserCodes: props.selectedCreateUserCodes.slice(0),
            selectedHandlerUserCodes: props.selectedHandlerUserCodes.slice(0),
            selectedObservers: props.selectedObservers.slice(0),
            selectedDynamicTimeFilterId: props.selectedDynamicTimeFilterId,
            startEndDateDisabled: props.selectedDynamicTimeFilterId !== props.dynamicTimeFilters[0].id
        };
    };

    constructor(props: IEventPageFilterDrawerProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: IEventPageFilterDrawerProp, prevState: IEventPageFilterDrawerState): void {
        const props = this.props;
        if (!prevProps ||
            (prevProps.filter?.getHash() === props.filter?.getHash() &&
            prevProps.selectedStartDate === props.selectedStartDate &&
            prevProps.selectedEndDate === props.selectedEndDate &&
            prevProps.selectedDynamicTimeFilterId === props.selectedDynamicTimeFilterId &&
            Base.equalStrArrays(prevProps.selectedUnitIds, props.selectedUnitIds) &&
            Base.equalStrArrays(prevProps.selectedStateIds, props.selectedStateIds) &&
            Base.equalStrArrays(prevProps.selectedUrgencyClassIds, props.selectedUrgencyClassIds) &&
            Base.equalStrArrays(prevProps.selectedEventTypeIds, props.selectedEventTypeIds) &&
            Base.equalStrArrays(prevProps.selectedEventTypeBIds, props.selectedEventTypeBIds) &&
            Base.equalStrArrays(prevProps.selectedEventTypeCIds, props.selectedEventTypeCIds) &&
            Base.equalStrArrays(prevProps.selectedPartnerCompanyIds, props.selectedPartnerCompanyIds) &&
            Base.equalStrArrays(prevProps.selectedCreateUserCodes, props.selectedCreateUserCodes) &&
            Base.equalStrArrays(prevProps.selectedHandlerUserCodes, props.selectedHandlerUserCodes) &&
            Base.equalStrArrays(prevProps.selectedObservers, props.selectedObservers))) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "selectedDynamicTimeFilterId") {
            if (value.toInteger() > 0) {
                this.setState({ selectedDynamicTimeFilterId: value, startDateStr: "", endDateStr: "", startEndDateDisabled: true });
            } else {
                this.setState({ selectedDynamicTimeFilterId: value, startEndDateDisabled: false });
            }
        }
    };

    handleStartDateChange = (value: string) => {
        this.setState({ startDateStr: value });
    };

    handleEndDateChange = (value: string) => {
        this.setState({ endDateStr: value });
    };

    getSaveDataFromState = (props: IEventPageFilterDrawerProp, state: IEventPageFilterDrawerState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("startDate", state.startDateStr);
        result.append("endDate", state.endDateStr);
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedUrgencyClassIds", state.selectedUrgencyClassIds);
        result.append("selectedEventTypeIds", state.selectedEventTypeIds);
        result.append("selectedEventTypeBIds", state.selectedEventTypeBIds);
        result.append("selectedEventTypeCIds", state.selectedEventTypeCIds);
        result.append("selectedPartnerCompanyIds", state.selectedPartnerCompanyIds);
        result.append("selectedCreateUserCodes", state.selectedCreateUserCodes);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedObservers", state.selectedObservers);
        result.append("selectedDynamicTimeFilterId", state.selectedDynamicTimeFilterId ? state.selectedDynamicTimeFilterId : null);
        return result;
    };

    static filterIsSet = (filterLists: IEventPageFilterLists, filterState: IEventPageFilterState): boolean => {
        return !!filterState.selectedStartDate || !!filterState.selectedEndDate ||
            !(Base.equalStrArrays(filterState.selectedUnitIds, [filterLists.units[0].id]) &&
            Base.equalStrArrays(filterState.selectedStateIds, [filterLists.states[0].id]) &&
            Base.equalStrArrays(filterState.selectedUrgencyClassIds, [filterLists.urgencyClasses[0].id]) &&
            Base.equalStrArrays(filterState.selectedEventTypeIds, [filterLists.eventTypes[0].id]) &&
            Base.equalStrArrays(filterState.selectedEventTypeBIds, [filterLists.eventTypeBs[0].id]) &&
            Base.equalStrArrays(filterState.selectedEventTypeCIds, [filterLists.eventTypeCs[0].id]) &&
            Base.equalStrArrays(filterState.selectedPartnerCompanyIds, [filterLists.partners[0].id]) &&
            Base.equalStrArrays(filterState.selectedCreateUserCodes, [filterLists.creators[0].id]) &&
            Base.equalStrArrays(filterState.selectedHandlerUserCodes, [filterLists.handlers[0].id]) &&
            Base.equalStrArrays(filterState.selectedObservers, [filterLists.observers[0].id]) &&
            filterState.selectedDynamicTimeFilterId === filterLists.dynamicTimeFilters[0].id);
    };

    handleSaveFilter = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        const selectedStartDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const selectedEndDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        if (!saveAs) {
            props.onOk(false, cloneEventPageFilterState({ ...state, selectedStartDate, selectedEndDate }));
            await this.props.onSaveFilter(false, null);
        } else {
            await this.props.onSaveFilter(true, () => {
                return props.onOk(false, cloneEventPageFilterState({ ...state, selectedStartDate, selectedEndDate }));
            });
        }
    };

    handleClear = () => {
        const props = this.props;
        this.setState({
            startDateStr: "",
            endDateStr: "",
            selectedUnitIds: [props.units[0].id],
            selectedStateIds: [props.states[0].id],
            selectedUrgencyClassIds: [props.urgencyClasses[0].id],
            selectedEventTypeIds: [props.eventTypes[0].id],
            selectedEventTypeBIds: [props.eventTypeBs[0].id],
            selectedEventTypeCIds: [props.eventTypeCs[0].id],
            selectedPartnerCompanyIds: [props.partners[0].id],
            selectedCreateUserCodes: [props.creators[0].id],
            selectedHandlerUserCodes: [props.handlers[0].id],
            selectedObservers: [props.observers[0].id],
            selectedDynamicTimeFilterId: props.dynamicTimeFilters[0].id
        });
    };

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        const selectedStartDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const selectedEndDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        props.onOk(true, cloneEventPageFilterState({ ...state, selectedStartDate, selectedEndDate }));
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <PageFilterConditionDrawer
                open={props.open}
                onClose={() => this.handleCancel(false)}
            >
                <DrawerContent
                    content={
                        <>
                            {!!props.onSaveFilter && !!props.onRemoveFilter &&
                            <FilterSearchField
                                filter={props.filter}
                                canRemoveSharedFilters={props.canRemoveSharedFilters}
                                onSaveFilter={this.handleSaveFilter}
                                onRemoveFilter={props.onRemoveFilter}
                            />
                            }
                            <FormSectionTitle>{Translations.Event}</FormSectionTitle>
                            <FormField title={Translations.Date} margin={0}>
                                <GridRow>
                                    <Grid item sm={6}>
                                        {!state.startEndDateDisabled &&
                                            <DatePicker
                                                value={state.startDateStr}
                                                onChange={this.handleStartDateChange}
                                            />
                                        }
                                        {state.startEndDateDisabled &&
                                            <TextField type="text" title={Translations.Date} disabled={true} />
                                        }
                                    </Grid>
                                    <Grid item sm={6}>
                                        {!state.startEndDateDisabled &&
                                            <DatePicker
                                                value={state.endDateStr}
                                                onChange={this.handleEndDateChange}
                                            />
                                        }
                                        {state.startEndDateDisabled &&
                                            <TextField type="text" title={Translations.Date} disabled={true} />
                                        }
                                    </Grid>
                                    <Grid item sm={12}>
                                        <Select name="selectedDynamicTimeFilterId" title={Translations.Date} value={state.selectedDynamicTimeFilterId} onChange={this.handleChange} fullWidth>
                                            {props.dynamicTimeFilters.map(dynamicTimeFilter =>
                                                <MenuItem key={dynamicTimeFilter.id} value={dynamicTimeFilter.id}>{dynamicTimeFilter.title}</MenuItem>
                                            )}
                                        </Select>
                                    </Grid>
                                </GridRow>
                            </FormField>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Unit}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedUnitIds}
                                            values={props.units}
                                            onChange={(selectedUnitIds: string[]) => this.setState({ selectedUnitIds })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.State}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedStateIds}
                                            toggleAll={true}
                                            toggleAllId={EventStateFilter.All.toString(10)}
                                            values={props.states}
                                            onChange={(selectedStateIds: string[]) => this.setState({ selectedStateIds })}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.EventUrgencyClass}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedUrgencyClassIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.urgencyClasses}
                                            onChange={(selectedUrgencyClassIds: string[]) => this.setState({ selectedUrgencyClassIds })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Partner}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedPartnerCompanyIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.partners}
                                            onChange={(selectedPartnerCompanyIds: string[]) => this.setState({ selectedPartnerCompanyIds })}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.EventCreator}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedCreateUserCodes}
                                            toggleAll={true}
                                            toggleAllId={""}
                                            values={props.creators}
                                            onChange={(selectedCreateUserCodes: string[]) => this.setState({ selectedCreateUserCodes })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.EventHandler}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedHandlerUserCodes}
                                            toggleAll={true}
                                            toggleAllId={""}
                                            values={props.handlers}
                                            onChange={(selectedHandlerUserCodes: string[]) => this.setState({ selectedHandlerUserCodes })}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Observer}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedObservers}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.observers}
                                            onChange={(selectedObservers: string[]) => this.setState({ selectedObservers })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                </Grid>
                            </GridRow>
                            <FormSectionTitle>{Translations.Identification}</FormSectionTitle>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.EventType} margin={0}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            toggleNotSetId={EventTypeNotSetId}
                                            selectedIds={state.selectedEventTypeIds}
                                            values={props.eventTypes}
                                            onChange={(selectedEventTypeIds: string[]) => this.setState({ selectedEventTypeIds })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.EventTypeB} margin={0}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            toggleNotSetId={EventTypeNotSetId}
                                            selectedIds={state.selectedEventTypeBIds}
                                            values={props.eventTypeBs}
                                            onChange={(selectedEventTypeBIds: string[]) => this.setState({ selectedEventTypeBIds })}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.EventTypeC}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            toggleNotSetId={EventTypeNotSetId}
                                            selectedIds={state.selectedEventTypeCIds}
                                            values={props.eventTypeCs}
                                            onChange={(selectedEventTypeCIds: string[]) => this.setState({ selectedEventTypeCIds })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                </Grid>
                            </GridRow>
                        </>
                    }
                    actions={
                        <FilterButtons
                            onSave={this.handleSave}
                            onCancel={() => this.handleCancel(false)}
                            onClear={this.handleClear}
                        />
                    }
                />
            </PageFilterConditionDrawer>
        );
    }
}